import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import CommonGrid from "../CommonGrid";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../redux/api";
import { FormControl, TextField } from "@mui/material";
import { cloneDeep, debounce } from "lodash";
import { TRACKING_REPORT_DATA_TYPE } from "../../../constants/constantList";
import { t } from "i18next";
import LabeledCheckbox from "../../TemplateProperties/TPCheckbox";
import LightTooltip from "../../../commonComponents/LightTooltip";
import { customEncoding } from "../../../utils/utilFunctions";

const TrackingReportDetails = ({selectedTR, setDetailsGridAPI, fieldsChangeHandler, TRLoading, setSnackbar, disableModification, detailsGridAPI, setEnableApply}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const {account, user} = useSelector(state => state.queryParams);
	const [rowData, setRowData] = useState([]);
	const [loading, setLoading] = useState(false);

	// Debounced snackbar function to prevent rapid successive error messages
	const debouncedSetSnackbar = useCallback(
		debounce((snackbarConfig) => {
			setSnackbar(snackbarConfig);
		}, 300),
		[setSnackbar]
	);
	const onChangeHandler = (key, value, api) => {

	}


	const gridOptions = {
		columnDefs: [
			{
				showDeleteIconOnly:true,
				isDeleteControl: true,
				editable: false,
				deleteHandler: (data) => {
					setEnableApply(true)
				},
				showDeleteIcon: (data) => {
				  return  data?.Field_Name;
				},
				headerClass: "first-column-header",
			},
			{
				headerName: t("TBL_FLD_NAME"),
				field: "Field_Name",
				editable: true,
				cellEditor: CustomTextField,
				cellEditorParams: {
					fieldsChangeHandler: fieldsChangeHandler
				},
			},
			{
				headerName: t("DATA_TYPE"),
				field: "Data_Type",
				cellEditor: CustomSelectField,
				cellEditorParams: {
					fieldsChangeHandler: fieldsChangeHandler
				},
				editable: (params) => {
					return params.data.Field_Name;
				},
				width: 100
			},
			{
				headerName: t("SIZE_"),
				field: "Size",
				cellEditor: CustomTextField,
				cellEditorParams: {
					fieldsChangeHandler: fieldsChangeHandler,
					type: "number"
				},
				editable: (params) => {
					return params.data.Field_Name && params.data.Data_Type && ["varchar", "integer", "bigint"].includes(params.data.Data_Type);
				},
				width: 100
			},
			{
				headerName: t("LBL_DATA_IS_INSERT"),
				field: "Is_Insert",
				cellRenderer: CustomCheckbox,
				cellRendererParams: {
					fieldsChangeHandler: fieldsChangeHandler
				},
				width: 70
			},
			{
				headerName: t("LBL_DATA_IS_UPDATE"),
				field: "Is_Update",
				cellRenderer: CustomCheckbox,
				cellRendererParams: {
					fieldsChangeHandler: fieldsChangeHandler
				},
				width: 50
			},
			{
				headerName: t("LBL_DATA_IS_KEY"),
				field: "Is_Key",
				cellRenderer: CustomCheckbox,
				cellRendererParams : {
					fieldsChangeHandler: fieldsChangeHandler
				},
				width: 50
			},
		],
	};

	const checkDuplicateKey = (id, value, gridApi) => {
		if (!value) return false;
		let duplicateFound = false;
		gridApi.forEachNode((node) => {
		  if (node.data.id !== id && node.data.Field_Name?.toLowerCase() === value?.toLowerCase()) duplicateFound = true;
		});
		return duplicateFound;
	  };
	
	const checkEmptyKey = (data, oldValue) => {
		if ((data.inititlaData && !data.Field_Name) ) 
		return {isEmptyKey: true,  valueToKeep: oldValue}
		return {isEmptyKey: false}
	}

	const handleCellValueChanged = (gridOptions) => {
		const colId = gridOptions.column.colId;
		const newValue = gridOptions.data[colId];
		const sizeType = gridOptions.data?.Data_Type || "";
		const rowIndex = gridOptions.rowIndex;
		var rowNode = gridOptions.api.getDisplayedRowAtIndex(rowIndex);

		if (colId === "Field_Name") {
			const id = gridOptions.data.id;
			const newValue = gridOptions.data.Field_Name;
			const rowIndex = gridOptions.rowIndex;
			const isDuplicateKey = checkDuplicateKey(id, newValue, gridOptions.api);
			const { isEmptyKey, valueToKeep } = checkEmptyKey(
				gridOptions.data,
				gridOptions.oldValue
			);
			const regex = /^[0-9a-zA-Z$_]+$/;
			if (newValue?.length > 64) {
				debouncedSetSnackbar({
					isOpen: true,
					severity: "error",
					message: t("ERROR_TRACKING_REPORT_FIELD_LIMIT")?.replaceAll("\\n", " "),
				});
				rowNode.setDataValue(colId, newValue?.slice(0, 64));
			}
			else if (!regex.test(newValue) && newValue) {
				debouncedSetSnackbar({
					isOpen: true,
					severity: "error",
					message: t("ERROR_TRACKING_REPORT_FIELD_CHARACTER_ALLOWED")?.replaceAll("\\n", " "),
				});
				rowNode.setDataValue(colId, gridOptions.oldValue);
			}
			else if (
				newValue?.toLowerCase() === "id" ||
				newValue?.toLowerCase() === "bg_timestamp" ||
				newValue?.toLowerCase() === "trans_id"
			) {
				debouncedSetSnackbar({
					isOpen: true,
					severity: "error",
					message: t("BOX_ERR_RESERVED_DD_FIELD_NAME")?.replaceAll("\\n", " "),
				});
				rowNode.setDataValue(colId, gridOptions?.oldValue || "");
			} else if (isDuplicateKey) {
				const rowNode = gridOptions.api.getDisplayedRowAtIndex(rowIndex);
				debouncedSetSnackbar({
					isOpen: true,
					severity: "error",
					message: t("BOX_ERR_DUPLICATE_DD_FIELD_NAME")?.replaceAll("\\n", " "),
				});
				rowNode.setDataValue(colId, gridOptions?.oldValue || "");
			} else if (isEmptyKey) {
				const rowNode = gridOptions.api.getDisplayedRowAtIndex(rowIndex);
				debouncedSetSnackbar({
					isOpen: true,
					severity: "error",
					message: t("BOX_ERR_EMPTY_DD_FIELD_NAME")?.replaceAll("\\n", " "),
				});
				if (valueToKeep) rowNode.setDataValue(colId, valueToKeep);
			} else {
				// Check if this was the last empty row and add a new empty row if needed
				const allRows = [];
				gridOptions.api.forEachNode((node) => allRows.push(node.data));
				const lastRow = allRows[allRows.length - 1];
				if (lastRow && lastRow.Field_Name && !disableModification) {
					// Last row now has a Field_Name, add a new empty row
					setRowData([...allRows, { id: allRows.length }]);
				}
			}
		}else if (colId === "Size") {
			const limit = {
				varchar: 16472,
				integer: 255,
				bigint: 255,
			}
			const error = {
				varchar: "ERR_TRACKING_REPORT_VARCHAR_SIZE_LIMIT",
				integer: "ERR_TRACKING_REPORT_INTEGER_SIZE_LIMIT",
				bigint: "ERR_TRACKING_REPORT_BIGINT_SIZE_LIMIT",
			}
			if (newValue > limit[sizeType]) {
				debouncedSetSnackbar({
					isOpen: true,
					severity: "error",
					message:t(error[sizeType]),
				});
				rowNode.setDataValue(colId, gridOptions.oldValue);
			}
		}
		fieldsChangeHandler()
	};

	const getStructuredTRFields = (fields) => {
		const newFields = fields?.map((e, i) => {
			const field = {...e, id: i,Data_Type: e?.Data_Type?.toLowerCase(), initialData: e, Size: e?.Size === "0" ? "" : e?.Size};
			return field;
		})
		return newFields;
	}

	const getTrackingReportFields = async (reportName) => {
		setRowData([]);
		setLoading(true);
		try{
			const data = await dispatch(api.endpoints.getTrackingReportFields.initiate({
				account,
				user,
				reportName: customEncoding(reportName),
			}, {forceRefetch: true}))
			const fields = [...cloneDeep(data?.data?.FieldNames || [])]
			if(!disableModification) fields.push({});
			setRowData(getStructuredTRFields(fields));
			setLoading(false);
		}catch(err) {
			setLoading(false);
			debouncedSetSnackbar({
				isOpen:true,
				severity:'error',
				message:err?.data?.message || t("BOX_MSG_GENERAL_ERROR")
			})
		}
	}

	useEffect(() => {
		if (detailsGridAPI && disableModification) {
			detailsGridAPI.setGridOption("columnDefs", [
				{
					headerName: t("TBL_FLD_NAME"),
					field: "Field_Name",
					editable: false,
					cellClass: "disable-element",
					cellEditor: CustomTextField,
					cellEditorParams: {
						fieldsChangeHandler: fieldsChangeHandler
					},
				},
				{
					headerName: t("DATA_TYPE"),
					field: "Data_Type",
					cellEditor: CustomSelectField,
					cellEditorParams: {
						fieldsChangeHandler: fieldsChangeHandler
					},
					editable: false,
					cellClass: "disable-element",
					width: 100
				},
				{
					headerName: t("SIZE_"),
					field: "Size",
					cellEditor: CustomTextField,
					cellEditorParams: {
						fieldsChangeHandler: fieldsChangeHandler,
						type: "number"
					},
					editable: false,
					cellClass: "disable-element",
					width: 100
				},
				{
					headerName: t("LBL_DATA_IS_INSERT"),
					field: "Is_Insert",
					cellRenderer: CustomCheckbox,
					cellRendererParams: {
						fieldsChangeHandler: fieldsChangeHandler,
						disableModification: disableModification,
					},
					cellClass: "disable-element",
					width: 70
				},
				{
					headerName: t("LBL_DATA_IS_UPDATE"),
					field: "Is_Update",
					cellRenderer: CustomCheckbox,
					cellRendererParams: {
						fieldsChangeHandler: fieldsChangeHandler,
						disableModification: disableModification,
					},
					cellClass: "disable-element",
					width: 50
				},
				{
					headerName: t("LBL_DATA_IS_KEY"),
					field: "Is_Key",
					cellRenderer: CustomCheckbox,
					cellRendererParams : {
						fieldsChangeHandler: fieldsChangeHandler,
						disableModification: disableModification,
					},
					cellClass: "disable-element",
					width: 50
				},
			])
		}
	}, [disableModification, detailsGridAPI])

	useEffect(() => {
		if(selectedTR && !TRLoading) getTrackingReportFields(selectedTR);
		else setRowData([]);
	},[selectedTR, TRLoading, disableModification])

	return (
		<div>
			<CommonGrid
				customLoadingClass={"tracking-report-fields-loading"}
				isLoading={loading}
				rowData={rowData}
				gridOptions={gridOptions}
				containerClasses='trading-partner-grids'
				getGridAPI={(e, gridRef) => {
					setDetailsGridAPI(gridRef.api);
				}}
				gridProps={{
					onRowDataUpdated: () => {
						// fieldsChangeHandler();
					},
					suppressHorizontalScroll: true,
					suppressScrollOnNewData: true,
					suppressScrollWhenPopupsAreOpen: true,
					// onCellKeyDown:onCellKeyDown,
					animateRows: false,
					isExternalFilterPresent: () => true,
					doesExternalFilterPass: (node) => {
						// Hide rows with Field_Name "id" or "bg_timestamp" from UI
						const fieldName = node.data?.Field_Name?.toLowerCase();
						return fieldName !== "id" && fieldName !== "bg_timestamp";
					},
					onCellClicked: (gridOptions) => {
						// Auto-set varchar 200 for new field names when clicked
						if (gridOptions.column.colId === "Field_Name" && 
							!Object.keys(gridOptions.data?.initialData || {}).length && 
							!gridOptions.data.Data_Type) {
							// Set data type to varchar and size to 200 for new fields
							gridOptions.data.Data_Type = "varchar";
							gridOptions.data.Size = "200";
							// Refresh the grid to show the updated values
							gridOptions.api.refreshCells({
								rowNodes: [gridOptions.node],
								columns: ["Data_Type", "Size"],
								force: true
							});
						}
						
						// Start editing with a delay to ensure grid is ready
						setTimeout(() => {
							gridOptions.api.startEditingCell({
								rowIndex: gridOptions.rowIndex,
								colKey: gridOptions.column.colId
							});
						}, 50);
					},
					onCellValueChanged: handleCellValueChanged,
					enableCellTextSelection: true,
					ensureDomOrder: true
				}}
			/>
		</div>
	);
};

export default TrackingReportDetails;


const CustomTextField = forwardRef((props, ref) => {
	const {onValueChange, eventKey, rowIndex, column, fieldsChangeHandler } = props
	const [value, setValue] = useState(props.value || '');
	const [disable, setDisable] = useState(false);
	const inputRef = useRef(null);
	
	const handleChange = (event) => {
		const newValue = event.target.value;
		setValue(newValue);
		onValueChange(newValue)
	};

	// Expose the value to Ag-Grid when editing is finished
	useImperativeHandle(ref, () => ({
	  getValue: () => value,
	  // Add focus method to ensure input gets focus
	  focus: () => {
		if (inputRef.current) {
		  inputRef.current.focus();
		  inputRef.current.select();
		}
	  }
	}));
	
	useEffect(() => {
		if (props?.colDef?.headerName === "Size") {
			if (["varchar", "integer", "bigint"].includes(props?.data?.Data_Type)) setDisable(false);
			else setDisable(true);
		}else {
			setDisable(false);
		}
	},[])

	// Auto-focus when component mounts
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, []);

	return (
	  <input
		ref={inputRef}
		onKeyPress={(e) => {
			if (props.type === "number" && !/[0-9]/.test(e.key)) {
				e.preventDefault(); // Block non-numeric characters
			}
		}}
		disabled={disable}
		type={props.type || "text"}
		value={disable ? "" : value}
		onChange={handleChange}
		style={{
		  width: '100%',
		  height: '100%',
		  boxSizing: 'border-box',
		  border: "none",
		  fontSize: "14px"
		}}
		placeholder={props?.colDef?.headerName}
		className="ag-fld-type-cell"
	  />
	);
});

const CustomSelectField = forwardRef((props, ref) => {
	const dropdown = TRACKING_REPORT_DATA_TYPE;
	const {t} = useTranslation();
	const [value, setValue] = useState(props.value || "");
	const selectRef = useRef(null);
  
	const handleChange = (event) => {
	  const newType = event.target.value;
	  setValue(newType);
	  props.onValueChange(newType?.toLowerCase());
	  props.fieldsChangeHandler();
	  
	  // Set size based on data type
	  if (["text", "float", "date", "datetime"].includes(newType)) {
		props.data["Size"] = "";
	  } else if (["varchar", "integer"].includes(newType) && !Object.keys(props?.data?.initialData || {})?.length && value === "") {
		props.data["Size"] = {
			"varchar": "200",
			"integer": "11",
		}[newType];
	  }
	  
	  // Refresh the grid to show updated size
	  props.api.refreshCells({
		rowNodes: [props.node],
		columns: ["Size"],
		force: true
	  });
	  
	  props.api.stopEditing();
	};
  
	useImperativeHandle(ref, () => ({
	  getValue: () => value,
	  // Add focus method to ensure select gets focus
	  focus: () => {
		if (selectRef.current) {
		  selectRef.current.focus();
		}
	  }
	}));
	const options = useMemo(() => {
		// For newly created fields (no initialData), show all dropdown options
		if (!Object.keys(props?.data?.initialData || {}).length) {
			return dropdown;
		}
		// For existing fields, follow current logic
		if(props.value) {
			return [
				{text: props.value?.toLowerCase(), value: value.toUpperCase()},
				{text: "ALPHA", value: "varchar"}
			];
		}
		// Fallback to dropdown if no value
		return dropdown;
	}, [value, props?.data?.initialData, dropdown])


	// Auto-focus when component mounts
	useEffect(() => {
		if (selectRef.current) {
			selectRef.current.focus();
		}
	}, []);

	return (
	  <select 
		ref={selectRef}
		style={{outline: "none"}} 
		className="ag-fld-type-cell" 
		value={value} 
		onChange={handleChange}
	  >
		{options.map((option) => (
		  <option key={option.value} value={option.value}>
			{t(option.text)}
		  </option>
		))}
	  </select>
	);
  });


  const CustomCheckbox = forwardRef((props, ref) => {
		const [isChecked, setIsChecked] = useState(props.value === "true" || "");
		const handleCheckboxChange = (value) => {
			setIsChecked(value);
			props.setValue(value ? "true" : "false");
			props.fieldsChangeHandler();
		};
		useImperativeHandle(ref, () => ({
			getValue: () => isChecked,
		}));
		return (
				<div style={{display: "flex",  alignContent: "center", alignItems: "center",justifyContent: "center"}}>
					<LabeledCheckbox
						checked={isChecked}
						handleCheck={handleCheckboxChange}
						disabled={(!props.data.Field_Name || props?.disableModification)}
					/>
				</div>
		);
	});
